import React, { FunctionComponent, useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Spinner from 'src/components/Spinner';
import { IWorkspaceSetting } from 'src/hooks/useWorkspaceSettings';

export interface IClaudeCredentialValues {
    apiKey: string
}

// Where the user generates an Anthropic API key. Kept as a single constant so it
// can be repointed (e.g. to a docs/help article) without hunting through JSX.
const CLAUDE_API_KEY_DOCS_URL = 'https://platform.claude.com/docs/en/manage-claude/admin-api-keys'

export interface IAddClaudeCredentialModalProps {
    isOpen: boolean
    apiKeyDefinition?: IWorkspaceSetting
    handleClose: () => void
    handleValidate: (values: IClaudeCredentialValues) => Promise<{ ok: boolean; message: string }>
    handleSaved: (values: IClaudeCredentialValues) => void
}

//
// Entry modal for the workspace's Claude (Anthropic) API key (MEL-574). Clones the
// Datadog credential modal, minus site/service/validate — the key is only staged
// here and persisted with the workspace PUT (masked, write-only, never read back).
//
const AddClaudeCredentialModal: FunctionComponent<IAddClaudeCredentialModalProps> = ({
    isOpen,
    apiKeyDefinition,
    handleClose,
    handleValidate,
    handleSaved,
}) => {
    const [apiKey, setApiKey] = useState('')
    const [apiKeyError, setApiKeyError] = useState<string | undefined>(undefined)
    const [isValidating, setIsValidating] = useState(false)

    useEffect(() => {
        if (isOpen) {
            setApiKey('')
            setApiKeyError(undefined)
            setIsValidating(false)
        }
    }, [isOpen])

    const handleSave = async () => {
        const trimmed = apiKey.trim()
        if (!trimmed) {
            setApiKeyError('API key is required')
            return
        }
        setApiKeyError(undefined)
        setIsValidating(true)
        try {
            // confirm the key is usable against Anthropic before staging it, so a mistyped or
            // revoked key is never saved as the workspace's diagnostics key
            const result = await handleValidate({ apiKey: trimmed })
            if (result.ok) {
                handleSaved({ apiKey: trimmed })
                handleClose()
            } else {
                setApiKeyError(result.message || 'This API key could not be validated')
            }
        } catch (e) {
            setApiKeyError('Could not reach the validation service. Please try again.')
        } finally {
            setIsValidating(false)
        }
    }

    return (
        <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle disableTypography style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">Add credential</Typography>
                <IconButton aria-label="Close" size="small" onClick={handleClose} disabled={isValidating}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <div className="field" style={{ marginTop: 8, marginBottom: 16 }}>
                    <Typography variant="subtitle2" style={{ marginBottom: 4 }}>{apiKeyDefinition?.label || 'Claude API Key'}</Typography>
                    <Typography variant="caption" color="textSecondary" component="p" style={{ margin: '0 0 12px', lineHeight: 1.5 }}>
                        Generate and paste your API key here. This API key will be masked and can only be
                        seen one-time.{' '}
                        <a
                            data-testid="claude-generate-key-link"
                            href={CLAUDE_API_KEY_DOCS_URL}
                            target="_blank"
                            rel="noreferrer">
                            See how to generate a Claude API key here
                        </a>
                    </Typography>
                    <TextField
                        data-testid="claude-api-key"
                        autoFocus
                        fullWidth
                        required
                        variant="outlined"
                        placeholder="sk-ant-..."
                        type="password"
                        value={apiKey}
                        error={!!apiKeyError}
                        helperText={apiKeyError}
                        onChange={e => setApiKey(e.target.value)}
                    />
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                    <Button
                        data-testid="claude-save-credential"
                        variant="contained"
                        disableElevation
                        fullWidth
                        onClick={handleSave}
                        disabled={isValidating}
                        style={{ backgroundColor: isValidating ? undefined : '#7C56DC', color: '#fff', textTransform: 'none' }}>
                        <Spinner active={isValidating}>
                            Save credential
                        </Spinner>
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default AddClaudeCredentialModal
